import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { requestApiGetConjunctions } from '@/query/conjunctions/useQueryCdm';
import useWeeklyReportStore from '@/store/weeklyReportStore';

dayjs.extend(weekOfYear);

const fetchWeeklyMetrics = async () => {
  try {
    const today = dayjs();
    const currentWeek = today.utc().startOf('week');

    const week1 = currentWeek;
    const week2 = currentWeek.subtract(1, 'week');
    const week3 = currentWeek.subtract(2, 'week');
    const week4 = currentWeek.subtract(3, 'week');

    const week1Name = `SPACEMAP_CA_${week1.format('YYYYMMMDD').toUpperCase()}T1600_S1`;
    const week2Name = `SPACEMAP_CA_${week2.format('YYYYMMMDD').toUpperCase()}T1600_S1`;
    const week3Name = `SPACEMAP_CA_${week3.format('YYYYMMMDD').toUpperCase()}T1600_S1`;
    const week4Name = `SPACEMAP_CA_${week4.format('YYYYMMMDD').toUpperCase()}T1600_S1`;

    const urgentParamsNum = { dcaUpper: 1, limit: 1, page: 0 };
    const highParamsNum = { dcaLower: 1, dcaUpper: 5, limit: 1, page: 0 };
    const totalParamsNum = { dcaUpper: 10, limit: 1, page: 0 };
    const koreanParamsNum = {
      option: 'SIMPLE',
      log: 'www',
      country: 'skor',
      page: 0,
      limit: 100,
      mode: 'ALL',
    };

    const createWeekParams = (week: dayjs.Dayjs) => ({
      urgent: {
        ...urgentParamsNum,
        tcaStart: week.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        tcaEnd: week.add(3, 'day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
      },
      high: {
        ...highParamsNum,
        tcaStart: week.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        tcaEnd: week.add(3, 'day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
      },
      total: {
        ...totalParamsNum,
        tcaStart: week.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        tcaEnd: week.add(3, 'day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
      },
      korean: {
        ...koreanParamsNum,
        tcaStart: week.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        tcaEnd: week.add(3, 'day').format('YYYY-MM-DDTHH:mm:ss[Z]'),
      },
    });

    const week1Params = createWeekParams(week1);
    const week2Params = createWeekParams(week2);
    const week3Params = createWeekParams(week3);
    const week4Params = createWeekParams(week4);

    const promises = [
      requestApiGetConjunctions(week1Params.urgent, week1Name),
      requestApiGetConjunctions(week1Params.high, week1Name),
      requestApiGetConjunctions(week1Params.total, week1Name),
      requestApiGetConjunctions(week1Params.korean, week1Name),

      requestApiGetConjunctions(week2Params.urgent, week2Name),
      requestApiGetConjunctions(week2Params.high, week2Name),
      requestApiGetConjunctions(week2Params.total, week2Name),
      requestApiGetConjunctions(week2Params.korean, week2Name),

      requestApiGetConjunctions(week3Params.urgent, week3Name),
      requestApiGetConjunctions(week3Params.high, week3Name),
      requestApiGetConjunctions(week3Params.total, week3Name),
      requestApiGetConjunctions(week3Params.korean, week3Name),

      requestApiGetConjunctions(week4Params.urgent, week4Name),
      requestApiGetConjunctions(week4Params.high, week4Name),
      requestApiGetConjunctions(week4Params.total, week4Name),
      requestApiGetConjunctions(week4Params.korean, week4Name),
    ];

    const results = await Promise.allSettled(promises);

    const getCount = (result: any) => {
      if (result.status === 'fulfilled') {
        return result.value?.data?.totalCount ?? result.value?.totalCount ?? 0;
      }
      return 0;
    };

    return {
      thisWeek: {
        urgentRiskCount: getCount(results[0]),
        highRiskCount: getCount(results[1]),
        totalMonitoredCount: getCount(results[2]),
        koreanRiskCount: getCount(results[3]),
      },
      lastWeek: {
        urgentRiskCount: getCount(results[4]),
        highRiskCount: getCount(results[5]),
        totalMonitoredCount: getCount(results[6]),
        koreanRiskCount: getCount(results[7]),
      },
      twoWeeksAgo: {
        urgentRiskCount: getCount(results[8]),
        highRiskCount: getCount(results[9]),
        totalMonitoredCount: getCount(results[10]),
        koreanRiskCount: getCount(results[11]),
      },
      threeWeeksAgo: {
        urgentRiskCount: getCount(results[12]),
        highRiskCount: getCount(results[13]),
        totalMonitoredCount: getCount(results[14]),
        koreanRiskCount: getCount(results[15]),
      },
    };
  } catch (error) {
    console.error('Error in fetchWeeklyMetrics:', error);
    throw error;
  }
};

export const useWeeklyMetrics = (currentDate: dayjs.Dayjs) => {
  const [isFetchingMetrics, setIsFetchingMetrics] = useState(false);
  const { lastFetched, setWeeklyData, currentCalendarWeek, getWeekData } =
    useWeeklyReportStore();

  useEffect(() => {
    const actualCalendarWeek = dayjs().format('YYYY-MM-DD');
    const realCalendarWeek = dayjs(actualCalendarWeek).week();
    const shouldFetch =
      !lastFetched || currentCalendarWeek !== realCalendarWeek.toString();

    if (shouldFetch) {
      setIsFetchingMetrics(true);
      fetchWeeklyMetrics()
        .then(data => {
          setWeeklyData(data, actualCalendarWeek);
        })
        .catch(err => {
          console.error('Failed to fetch and set weekly metrics', err);
        })
        .finally(() => {
          setIsFetchingMetrics(false);
        });
    }
  }, []);

  const getWeekDataByDate = (targetDate: dayjs.Dayjs) => {
    const currentWeek = dayjs().utc().startOf('week');
    const weeksDiff = currentWeek.diff(
      targetDate.utc().startOf('week'),
      'week'
    );
    return getWeekData(weeksDiff);
  };

  return {
    thisWeek: getWeekDataByDate(currentDate),
    lastWeek: getWeekDataByDate(currentDate.subtract(1, 'week')),
    twoWeeksAgo: getWeekDataByDate(currentDate.subtract(2, 'week')),
    threeWeeksAgo: getWeekDataByDate(currentDate.subtract(3, 'week')),
    isFetchingMetrics,
  };
};
